import {
  type FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";
import { Toaster as Sonner } from "sonner";

import { ToastPauseContext } from "@/components/toast-card";
import { setToastRegion, showToastAfterReload } from "@/lib/toast";

function subscribeVisibility(onChange: () => void): () => void {
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}

/** Whether this tab is hidden (another tab in front, the window minimised). */
function useTabHidden(): boolean {
  return useSyncExternalStore(
    subscribeVisibility,
    () => document.visibilityState === "hidden",
    () => false,
  );
}

/**
 * The toasts' distance from the window's edges: `--toast-gap` (24 px, 16 px on
 * a phone), and at the top the view-as bar's height too while it shows
 * (`--toast-bar-inset`), both set in `src/styles/globals.css`. The same on a
 * phone and a wider screen: the gap itself follows sonner's phone break.
 */
const TOAST_OFFSET = {
  top: "calc(var(--toast-bar-inset) + var(--toast-gap))",
  right: "var(--toast-gap)",
  bottom: "var(--toast-gap)",
  left: "var(--toast-gap)",
};

/**
 * Where the dashboard's toasts appear (`toast` in `src/lib/toast.ts`), mounted
 * once at the root: top right, across the top on a phone, below the view-as
 * bar while it shows and above every dialog. The newest is on top, the older
 * ones under it; up to three show at a time, stacked, and the pointer on them
 * spreads them out downwards. While the pointer or the focus is on them, or
 * the tab is hidden, they wait. Alt+T moves the focus to them. On mount it
 * shows the toast the page before the last reload kept (`toastAfterReload`).
 */
export function Toaster() {
  const { t } = useTranslation();
  const region = useRef<HTMLElement | null>(null);
  /** Where the mouse or pen was last seen, for the check after a toast goes; null for touch or off the page. */
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const hidden = useTabHidden();

  const onRegion = useCallback((node: HTMLElement | null) => {
    region.current = node;
    setToastRegion(node);
  }, []);

  useEffect(() => {
    showToastAfterReload();
  }, []);

  // The pointer is followed on the whole page rather than by enter and leave
  // events, which never come when the toast under the pointer is removed.
  useEffect(() => {
    const inRegion = (node: EventTarget | Element | null) =>
      node instanceof Node && (region.current?.contains(node) ?? false);
    const onMove = (event: PointerEvent) => {
      // A finger does not hover: a tap on a toast never holds the others.
      pointer.current =
        event.pointerType === "touch" ? null : { x: event.clientX, y: event.clientY };
      setHovered(pointer.current !== null && inRegion(event.target));
    };
    const onOut = (event: PointerEvent) => {
      // Off the page: out of the window, or a finger lifted.
      if (event.relatedTarget === null) {
        pointer.current = null;
        setHovered(false);
      }
    };
    // When a toast is added or removed, read again whether the pointer and the
    // focus are on what is left. A toast removed under the pointer fires no
    // leave, and one removed with the focus in it fires no blur React hears.
    const recheck = () => {
      const at = pointer.current;
      setHovered(at !== null && inRegion(document.elementFromPoint(at.x, at.y)));
      setFocused(inRegion(document.activeElement));
    };
    const observer = new MutationObserver(recheck);
    if (region.current) {
      observer.observe(region.current, { childList: true, subtree: true });
    }
    document.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerout", onOut, { passive: true });
    return () => {
      observer.disconnect();
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerout", onOut);
    };
  }, []);

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFocused(false);
    }
  };

  return (
    <ToastPauseContext value={hovered || focused || hidden}>
      <div
        className="contents"
        onFocus={() => setFocused(true)}
        onBlur={onBlur}
        // A press on a toast is no press outside an open dialog or popover, which would close it.
        onPointerDown={(event) => event.stopPropagation()}
      >
        <Sonner
          ref={onRegion}
          position="top-right"
          offset={TOAST_OFFSET}
          mobileOffset={TOAST_OFFSET}
          containerAriaLabel={t("toast.region")}
          gap={12}
          visibleToasts={3}
          // Clickable while a dialog makes the rest of the page inert to the pointer. Cut off
          // above the gap, so a toast sliding in or out never crosses the view-as bar.
          className="pointer-events-auto [clip-path:inset(calc(-1*var(--toast-gap))_-100vw_-100vh_-100vw)]"
          toastOptions={{
            unstyled: true,
            // A little wider than the stack's default; a phone's toasts span the screen. In
            // the stack a toast behind the front one takes the front one's height: cut it to
            // that, so a longer one never shows below the stack. A toast reached with Tab
            // gets the theme's focus ring: sonner's own faint one sits outside every CSS
            // layer, so only an important utility outweighs it.
            className:
              "w-[400px] rounded-lg data-[expanded=false]:data-[front=false]:overflow-hidden focus-visible:shadow-none! focus-visible:outline-2! focus-visible:outline-offset-2! focus-visible:outline-ring!",
          }}
        />
      </div>
    </ToastPauseContext>
  );
}
